<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/process/packing/edit_harvested_batch.yml"
  lang="yaml"
></i18n>

<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      :name="routeName('show_harvested_batch')"
      :params="{
        organization_id: organization_id,
        harvested_batch_id: harvested_batch_id
      }"
    />
    <bread-crumbs :labels="[module_name, harvest_process.name]"></bread-crumbs>

    <alert-danger :errors_data="std_err" />

    <div class="mt-8" v-if="load_general_data">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.batch_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="harvested_batch.batch_code"
                    :class="['input-text', isInvalid('batch_code')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.registered_at") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="harvested_batch.registered_at" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.producer") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="producers.length > 1"
                    :options="producers"
                    v-model="harvested_batch.producer_id"
                    label="full_label"
                    :is_invalid="isInvalidSelect('producer')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_field") }}
                </label>
                <div class="mt-1" v-if="crop_fields.length > 1">
                  <select-form
                    :options="crop_fields"
                    v-model="harvested_batch.crop_field_id"
                    :is_invalid="isInvalidSelect('crop_field')"
                  />
                </div>
                <dd class="content-data" v-else-if="crop_fields.length == 1">
                  {{ crop_fields[0].name }}
                </dd>
              </div>
              <template
                v-for="(crop_har_package, index) in crop_har_packages"
                :key="crop_har_package.id"
              >
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_package.number") }}
                  </label>
                  <div class="flex mt-1 relative">
                    <input
                      type="text"
                      v-model.number.trim="harvested_packages[index].number"
                      :class="['input-text pr-10', isInvalid('number')]"
                    />
                    <div
                      class="flex items-center absolute inset-y-0 right-0 mr-3 text-sm leading-5"
                    >
                      {{ crop_har_package.name }}
                    </div>
                  </div>
                </div>
              </template>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.total_weight") }}
                </label>
                <div class="flex mt-1">
                  <input
                    type="text"
                    v-model.number.trim="harvested_batch.total_weight"
                    :class="['input-text', isInvalid('total_weight')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_type") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="crop_types.length > 1"
                    :options="crop_types"
                    v-model="harvested_batch.crop_type_id"
                    :is_invalid="isInvalidSelect('crop_type')"
                  />
                  <dd class="content-data" v-else-if="crop_types.length == 1">
                    {{ crop_types[0].name }}
                  </dd>
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_variety") }}
                </label>
                <div class="mt-1">
                  <dd class="content-data">
                    {{ crop_field?.crop_variety?.name }}
                  </dd>
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="agro_exporter_processes.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.harvested_batch.agro_exporter") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="agro_exporter_processes.length > 1"
                    :options="agro_exporter_processes"
                    v-model="harvested_batch.agro_exporter_process_id"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.harvest_team") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="harvest_teams"
                    v-model="harvested_batch.harvest_team_id"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.packing_line") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="packing_lines"
                    v-model="harvested_batch.packing_line_id"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowHarvestedBatch()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!charging_submit"
              @click.prevent="update"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import RequesterQuery from "@/views/organization/requesters/process/packing/queries_to_harvested_batch.js";
import Requester from "@/views/organization/requesters/process/packing/manage_harvested_batch.js";
import moment from "moment";

export default {
  name: "OrgProcessPackingNewHarvestedBatch",
  data() {
    return {
      load_general_data: false,
      charging_submit: false,
      harvest_process_id: null,
      harvest_process: {},
      original_harvested_batch: {},
      harvested_batch: {
        batch_code: "",
        total_weight: null,
        registered_at: null,
        crop_type_id: null,
        crop_variety_id: null,
        producer_id: null,
        agro_exporter_process_id: null,
        harvest_team_id: null,
        packing_line_id: null,
        crop_field_id: null
      },
      crop_types: [],
      crop_varieties: [],
      agro_exporter_processes: [],
      producers: [],
      crop_har_packages: [],
      harvested_packages: [],
      harvest_teams: [],
      packing_lines: [],
      crop_fields: []
    };
  },
  props: {
    organization_id: { required: true },
    harvested_batch_id: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() => {
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => {
        this.harvested_batch.registered_at = moment().format("YYYY-MM-DD");
        this.loadData();
      });
    });
  },
  computed: {
    harvested_packages_params: function () {
      return this.harvested_packages.filter(
        harvested_package =>
          Boolean(harvested_package.id) || Number(harvested_package.number) > 0
      );
    },
    crop_field: function () {
      return this.crop_fields.find(
        crop_field => crop_field.id == this.harvested_batch.crop_field_id
      );
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestBatch();
      await this.fetchHarvestProcess();
      await this.fetchCropTypes();
      await this.fetchAgroExporterProcesses();
      await this.fetchProducers();
      await this.fetchCropHarPackages();
      await this.fetchPackingLines();
      await this.fetchHarvestTeams();
      this.load_general_data = true;
    },
    fetchHarvestBatch: async function () {
      let response = await Requester.fetchHarvestedBatch(
        this.organization_id,
        this.harvested_batch_id
      );
      this.original_harvested_batch = response.data;
      this.fillHarvestedBatch();

      this.harvest_process_id =
        this.original_harvested_batch.harvest_process_id;
      this.harvested_packages =
        this.original_harvested_batch.harvested_packages;
    },
    fillHarvestedBatch: function () {
      this.harvested_batch = {
        batch_code: this.original_harvested_batch.batch_code,
        total_weight: this.original_harvested_batch.total_weight,
        registered_at: this.original_harvested_batch.registered_at,
        crop_type_id: this.original_harvested_batch.crop_type_id,
        crop_variety_id: this.original_harvested_batch.crop_variety_id,
        producer_id: this.original_harvested_batch.producer_id,
        agro_exporter_process_id:
          this.original_harvested_batch.agro_exporter_process_id,
        harvest_team_id: this.original_harvested_batch.harvest_team_id,
        packing_line_id: this.original_harvested_batch.packing_line_id,
        crop_field_id: this.original_harvested_batch.crop_field_id
      };
    },
    fetchHarvestProcess: async function () {
      const response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchCropTypes: async function () {
      const response = await RequesterQuery.fetchCropTypes(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_types = response.data;
    },
    fetchHarvestTeams: async function () {
      const response = await RequesterQuery.fetchHarvestTeams(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_teams = response.data;
      this.harvest_teams.forEach(team => {
        team["name"] = `${team.harvest_team_code} - ${team.foreman}`;
      });
    },
    fetchAgroExporterProcesses: async function () {
      const response = await RequesterQuery.fetchAgroExporterProcesses(
        this.organization_id,
        this.harvest_process_id
      );
      this.agro_exporter_processes = response.data;
      this.agro_exporter_processes = this.agro_exporter_processes.map(aep => {
        return {
          id: aep.id,
          name: `${aep.name} - ${aep.agro_exporter.name}`
        };
      });
    },
    fetchProducers: async function () {
      const response = await RequesterQuery.fetchProducers(
        this.organization_id
      );
      this.producers = response.data;
      this.producers.forEach(producer => {
        producer.full_label = `${producer.organization_producer_code} - ${producer.fullname}`;
      });
    },
    fetchPackingLines: async function () {
      const response = await RequesterQuery.fetchPackingLines(
        this.organization_id,
        this.harvest_process_id
      );
      this.packing_lines = response.data;
    },
    fetchCropHarPackages: async function () {
      const response = await RequesterQuery.fetchCropHarPackages(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_har_packages = response.data;
      this.harvested_packages = this.crop_har_packages.map(crop_har_package => {
        return {
          id: this.original_harvested_batch.harvested_packages.find(
            harvested_package =>
              harvested_package.crop_har_package_id === crop_har_package.id
          )?.id,
          crop_har_package_id: crop_har_package.id,
          number: this.original_harvested_batch.harvested_packages.find(
            harvested_package =>
              harvested_package.crop_har_package_id === crop_har_package.id
          )?.number
        };
      });
    },
    fetchCropFields: async function () {
      const response = await RequesterQuery.fetchCropFields(
        this.organization_id,
        this.harvested_batch.producer_id
      );
      this.crop_fields = response.data;
      if (this.crop_fields.length == 1) {
        this.harvested_batch.crop_field_id = this.crop_fields[0].id;
      }
      if (
        this.harvested_batch.producer_id ==
        this.original_harvested_batch.producer_id
      ) {
        this.harvested_batch.crop_field_id =
          this.original_harvested_batch.crop_field_id;
      }
    },
    update: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.update(
          this.organization_id,
          this.harvested_batch_id,
          this.harvest_process_id,
          {
            ...this.harvested_batch,
            harvested_packages: this.harvested_packages_params
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowHarvestedBatch();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.charging_submit = false;
    },
    goToShowHarvestedBatch: function () {
      this.$router.push({
        name: this.routeName("show_harvested_batch"),
        params: {
          organization_id: this.organization_id,
          harvested_batch_id: this.harvested_batch_id
        }
      });
    },
    hasOneItem: function (array) {
      return array.length == 1 ? array[0].id : null;
    }
  },
  watch: {
    harvested_packages: {
      handler: function () {
        this.harvested_packages.forEach(har_package => {
          if (isNaN(har_package.number)) har_package.number = "";
          if (har_package.number && !isNaN(har_package.number)) {
            har_package.number = Math.abs(Math.floor(har_package.number));
          }
        });
      },
      immediate: true,
      deep: true
    },
    "harvested_batch.producer_id": function (new_value) {
      this.harvested_batch.crop_field_id = null;
      this.harvested_batch.crop_variety_id = null;
      this.crop_fields = [];
      if (new_value) this.fetchCropFields();
    },
    "harvested_batch.crop_field_id": function () {
      this.harvested_batch.crop_variety_id = this.crop_field?.crop_variety_id;
    }
  }
};
</script>
